/**
 * Support desk ticket detail — overview, workflow, actions, activity (aligned with case detail).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  TextField,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  OpenInNew as OpenInNewIcon,
  Note as NoteIcon,
  Email as EmailIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  PostAdd as PostAddIcon,
  Business as BusinessIcon,
  AttachFile as AttachIcon,
  Delete as DeleteIcon,
  Visibility as PreviewIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  MergeType as MergeTypeIcon,
  Save as SaveIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { AccountContact } from '../types/account';
import apiService from '../services/api';
import { isQuillNoteEmpty, NOTE_QUILL_MODULES, stripHtml } from '../utils/noteQuill';
import {
  buildSupportDeskTicketEmailBodies,
  SUPPORT_DESK_EMAIL_BODY_PLACEHOLDER,
} from '../utils/supportDeskTicketEmail';
import { isMspAgreementServiceType } from '../utils/mspAgreement';
import { looksLikeHtml } from '../utils/htmlContent';
import EmailHtmlContent from '../components/EmailHtmlContent';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enAU } from 'date-fns/locale';
import { format } from 'date-fns';

const TICKET_EMAIL_PLACEHOLDERS = [
  '{{ticket_number}}',
  '{{subject}}',
  '{{description}}',
  '{{account_name}}',
  '{{assignee_name}}',
  '{{status}}',
  '{{priority}}',
  '{{category}}',
  '{{requester_email}}',
  '{{requester_name}}',
  '{{channel}}',
];

type ChipColor = 'default' | 'primary' | 'success' | 'warning' | 'error';

function getSupportDeskActivityIcon(activityType: string) {
  switch (activityType) {
    case 'note':
      return <NoteIcon sx={{ fontSize: 18 }} />;
    case 'email_sent':
    case 'customer_reply':
      return <EmailIcon sx={{ fontSize: 18 }} />;
    case 'status_change':
      return <EditIcon sx={{ fontSize: 18 }} />;
    case 'assignee_change':
      return <PersonIcon sx={{ fontSize: 18 }} />;
    case 'account_change':
      return <BusinessIcon sx={{ fontSize: 18 }} />;
    case 'created':
      return <PostAddIcon sx={{ fontSize: 18 }} />;
    case 'workflow_step_updated':
      return <EditIcon sx={{ fontSize: 18 }} />;
    case 'workflow_completed':
      return <CheckCircleIcon sx={{ fontSize: 18 }} />;
    case 'tickets_merged':
    case 'merged_into':
      return <MergeTypeIcon sx={{ fontSize: 18 }} />;
    default:
      return <NoteIcon sx={{ fontSize: 18 }} />;
  }
}

/** Row tint in the activity feed: internal notes light yellow, customer replies light blue; everything else unchanged.
 * Translucent so it reads well on light and dark themes. */
function supportDeskActivityBackground(activityType: string): string | undefined {
  if (activityType === 'note') return 'rgba(255, 213, 79, 0.22)';
  if (activityType === 'customer_reply') return 'rgba(66, 165, 245, 0.18)';
  return undefined;
}

function supportDeskActivityLabel(activityType: string): string {
  const map: Record<string, string> = {
    note: 'Note',
    email_sent: 'Email sent',
    customer_reply: 'Customer reply',
    created: 'Ticket created',
    status_change: 'Status change',
    assignee_change: 'Assignee change',
    account_change: 'Account link',
    workflow_step_updated: 'Workflow step',
    workflow_completed: 'Workflow complete',
    tickets_merged: 'Tickets merged',
    merged_into: 'Merged into parent',
  };
  if (map[activityType]) return map[activityType];
  return activityType.replace(/_/g, ' ');
}

type ServiceRow = {
  id: string;
  service_id: string;
  service_type: string;
  service_type_name?: string | null;
};

type AccountLinkOption = {
  id: string;
  display_name: string;
  sonic_account_number?: string | null;
};

function formatAccountLinkLabel(a: AccountLinkOption) {
  const num = a.sonic_account_number?.trim();
  return num ? `${num} — ${a.display_name || 'Account'}` : a.display_name || a.id;
}

/** Interpret API timestamptz as the UTC calendar date for the date-only picker. */
function dueResolveAtToPickerDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function dueResolveYmdFromApi(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const SupportDeskDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [activityComposerMode, setActivityComposerMode] = useState<'internal' | 'email'>('internal');
  const [timelineReplyTemplateId, setTimelineReplyTemplateId] = useState('');
  const [timelineReplySubject, setTimelineReplySubject] = useState('');
  const [users, setUsers] = useState<{ id: string; username: string; display_name: string }[]>([]);
  const [opts, setOpts] = useState<Record<string, { value: string; label: string; color?: string }[]>>({});
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [services, setServices] = useState<ServiceRow[]>([]);
  const [accountServiceId, setAccountServiceId] = useState('');
  const [mspUseHours, setMspUseHours] = useState(false);
  const [mspHoursInput, setMspHoursInput] = useState('');
  const [mspBalanceHint, setMspBalanceHint] = useState<string | null>(null);
  const [accountLink, setAccountLink] = useState<AccountLinkOption | null>(null);
  const [accountSearch, setAccountSearch] = useState('');
  const [accountOptions, setAccountOptions] = useState<AccountLinkOption[]>([]);
  const [ticketWorkflow, setTicketWorkflow] = useState<{
    id: string;
    template_name: string;
    started_at?: string;
    completed_at?: string;
    progress_percent: number;
    steps: { id: string; step_name: string; step_description?: string; sort_order: number; status: string }[];
  } | null>(null);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [applyWorkflowOpen, setApplyWorkflowOpen] = useState(false);
  const [workflowTemplates, setWorkflowTemplates] = useState<{ id: string; name: string }[]>([]);
  const [applyWorkflowTemplateId, setApplyWorkflowTemplateId] = useState('');
  const [applyWorkflowSubmitting, setApplyWorkflowSubmitting] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailCc, setEmailCc] = useState('');
  const [emailBcc, setEmailBcc] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState<{ id: string; name: string; subject?: string; body: string }[]>([]);
  const [selectedEmailTemplateId, setSelectedEmailTemplateId] = useState('');
  const [accountContacts, setAccountContacts] = useState<AccountContact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [requesterEditing, setRequesterEditing] = useState(false);
  const [requesterNameInput, setRequesterNameInput] = useState('');
  const [requesterEmailInput, setRequesterEmailInput] = useState('');
  const [savingRequester, setSavingRequester] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<{
    id: string;
    filename: string;
    content_type?: string | null;
  } | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewTextContent, setPreviewTextContent] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [emailViewOpen, setEmailViewOpen] = useState(false);
  const [emailViewData, setEmailViewData] = useState<{
    to: string;
    subject: string;
    cc?: string;
    bcc?: string;
    body?: string;
    body_html?: string;
    isReply?: boolean;
  } | null>(null);
  const [inlineSaving, setInlineSaving] = useState<'status' | 'priority' | 'assignee' | null>(null);
  const [upstreamTicketRef, setUpstreamTicketRef] = useState('');
  const [upstreamSaving, setUpstreamSaving] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const t = await apiService.getSupportDeskTicket(id, { includeActivity: true, includeAttachments: true });
      setTicket(t);
      if (t.has_unseen_update) {
        // Opening the ticket clears the 'new update' highlight in the overview.
        apiService.markSupportDeskTicketSeen(id).catch(() => {});
      }
      setStatus(t.status);
      setPriority(t.priority);
      setAssigneeId(t.assignee_id || '');
      setAccountServiceId(t.account_service_id || '');
      setMspUseHours(!!t.msp_use_hours);
      setMspHoursInput(t.msp_hours_reserved || '');
      setUpstreamTicketRef(t.upstream_ticket_ref || '');
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load ticket');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const fetchWorkflow = useCallback(async () => {
    if (!id) return;
    setWorkflowLoading(true);
    try {
      const w = await apiService.getSupportDeskTicketWorkflow(id);
      setTicketWorkflow({
        id: w.id,
        template_name: w.template_name,
        started_at: w.started_at,
        completed_at: w.completed_at,
        progress_percent: w.progress_percent,
        steps: (w.steps || []).map((s) => ({
          id: String(s.id),
          step_name: s.step_name,
          step_description: s.step_description,
          sort_order: s.sort_order,
          status: s.status,
        })),
      });
    } catch {
      setTicketWorkflow(null);
    } finally {
      setWorkflowLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) fetchWorkflow();
  }, [id, fetchWorkflow]);

  useEffect(() => {
    if (!ticket) return;
    if (ticket.account_id) {
      setAccountLink({
        id: ticket.account_id,
        display_name: ticket.account_display_name || 'Account',
        sonic_account_number: ticket.account_sonic_number ?? null,
      });
      setAccountSearch(
        formatAccountLinkLabel({
          id: ticket.account_id,
          display_name: ticket.account_display_name || 'Account',
          sonic_account_number: ticket.account_sonic_number ?? null,
        })
      );
    } else {
      setAccountLink(null);
      setAccountSearch('');
    }
  }, [ticket]);

  useEffect(() => {
    const q = accountSearch.trim();
    if (q.length < 2) {
      setAccountOptions([]);
      return;
    }
    const tmr = window.setTimeout(() => {
      apiService
        .getAccounts({ q, per_page: 25, page: 1 })
        .then((r: any) => {
          const rows = (r.accounts || []) as any[];
          setAccountOptions(
            rows.map((a) => ({
              id: a.id,
              display_name: a.display_name || '',
              sonic_account_number: a.sonic_account_number ?? null,
            }))
          );
        })
        .catch(() => setAccountOptions([]));
    }, 320);
    return () => window.clearTimeout(tmr);
  }, [accountSearch]);

  useEffect(() => {
    apiService.getUsers().then(setUsers).catch(() => {});
    apiService.getSupportDeskFieldOptionsGrouped().then(setOpts).catch(() => {});
    apiService
      .getSupportDeskTemplates(true)
      .then((r) => setEmailTemplates(r.templates || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (activityComposerMode !== 'email' || !emailTemplates.length || timelineReplyTemplateId) return;
    let tid = '';
    try {
      const s = sessionStorage.getItem('supportDeskLastReplyTemplateId');
      if (s && emailTemplates.some((t) => t.id === s)) tid = s;
    } catch {
      /* ignore */
    }
    if (!tid) tid = emailTemplates[0]?.id || '';
    if (!tid) return;
    setTimelineReplyTemplateId(tid);
    const t = emailTemplates.find((x) => x.id === tid);
    setTimelineReplySubject(t?.subject || '');
  }, [activityComposerMode, emailTemplates, timelineReplyTemplateId]);

  useEffect(() => {
    if (!ticket?.account_id) {
      setServices([]);
      return;
    }
    apiService.getAccountServices(ticket.account_id).then((r: any) => {
      setServices(r.services || r || []);
    }).catch(() => setServices([]));
  }, [ticket?.account_id]);

  useEffect(() => {
    if (!accountServiceId) {
      setMspBalanceHint(null);
      return;
    }
    const s = services.find((x) => x.id === accountServiceId);
    if (!s || !isMspAgreementServiceType(s.service_type, s.service_type_name)) {
      setMspBalanceHint(null);
      return;
    }
    let cancelled = false;
    apiService
      .getMspBalance(s.id)
      .then((b: any) => {
        if (!cancelled) {
          setMspBalanceHint(
            `MSP balance — available: ${b.available_total} h (granted ${b.total_grants}, reserved ${b.reserved_total})`
          );
        }
      })
      .catch(() => {
        if (!cancelled) setMspBalanceHint(null);
      });
    return () => {
      cancelled = true;
    };
  }, [accountServiceId, services]);

  const toChipColor = (c?: string | null): ChipColor =>
    (c && ['default', 'primary', 'success', 'warning', 'error'].includes(c) ? c : 'default') as ChipColor;

  const stOpts = opts.status || [];
  const prOpts = opts.priority || [];
  const statusLabelMap: Record<string, string> =
    stOpts.length > 0 ? Object.fromEntries(stOpts.map((o) => [o.value, o.label])) : {};
  const statusColorMap: Record<string, ChipColor> =
    stOpts.length > 0 ? Object.fromEntries(stOpts.map((o) => [o.value, toChipColor(o.color)])) : {};
  const priorityLabelMap: Record<string, string> =
    prOpts.length > 0 ? Object.fromEntries(prOpts.map((o) => [o.value, o.label])) : {};
  const priorityColorMap: Record<string, ChipColor> =
    prOpts.length > 0 ? Object.fromEntries(prOpts.map((o) => [o.value, toChipColor(o.color)])) : {};

  const formatDateTime = (s: string | null | undefined) => {
    if (!s) return '—';
    return new Date(s).toLocaleString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleInlineStatusChange = async (next: string) => {
    if (!id || !ticket || next === ticket.status) return;
    setInlineSaving('status');
    setError(null);
    try {
      await apiService.updateSupportDeskTicket(id, { status: next });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to update status');
    } finally {
      setInlineSaving(null);
    }
  };

  const handleInlinePriorityChange = async (next: string) => {
    if (!id || !ticket || next === ticket.priority) return;
    setInlineSaving('priority');
    setError(null);
    try {
      await apiService.updateSupportDeskTicket(id, { priority: next });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to update priority');
    } finally {
      setInlineSaving(null);
    }
  };

  const handleInlineAssigneeChange = async (next: string) => {
    const cur = ticket?.assignee_id || '';
    if (!id || next === cur) return;
    setInlineSaving('assignee');
    setError(null);
    try {
      await apiService.updateSupportDeskTicket(id, { assignee_id: next || undefined });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to update assignee');
    } finally {
      setInlineSaving(null);
    }
  };

  const submitActivityComposer = async () => {
    const trimmed = note.trim();
    if (!id || isQuillNoteEmpty(trimmed)) return;
    if (activityComposerMode === 'internal') {
      setSaving(true);
      setError(null);
      try {
        await apiService.addSupportDeskTicketActivity(id, { activity_type: 'note', content: trimmed });
        setNote('');
        await load();
      } catch (e: any) {
        setError(e?.response?.data?.detail || 'Failed to add note');
      } finally {
        setSaving(false);
      }
      return;
    }
    if (!ticket?.requester_email?.trim()) {
      setError('Requester email is missing; add one on the ticket before emailing the customer.');
      return;
    }
    if (ticket.merged_into_ticket_id) {
      setError('This ticket was merged into another; open the parent ticket to email the customer.');
      return;
    }
    if (!timelineReplyTemplateId) {
      setError('Select an email template for replies to the customer.');
      return;
    }
    if (!timelineReplySubject.trim()) {
      setError('Subject is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const tpl = emailTemplates.find((x) => x.id === timelineReplyTemplateId);
      const { body, body_html } = buildSupportDeskTicketEmailBodies(trimmed, tpl || undefined);
      await apiService.sendSupportDeskTicketEmail(id, {
        to_email: ticket.requester_email.trim(),
        subject: timelineReplySubject.trim(),
        body,
        body_html,
        template_id: timelineReplyTemplateId,
      });
      try {
        sessionStorage.setItem('supportDeskLastReplyTemplateId', timelineReplyTemplateId);
      } catch {
        /* ignore */
      }
      setNote('');
      const t = emailTemplates.find((x) => x.id === timelineReplyTemplateId);
      setTimelineReplySubject(t?.subject || '');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to send email');
    } finally {
      setSaving(false);
    }
  };

  const persistAccountLinkChange = async (newVal: AccountLinkOption | null) => {
    if (!id || !ticket) return;
    const newAccountId = newVal?.id ?? null;
    const curAccountId = ticket.account_id ?? null;
    if (newAccountId === curAccountId) return;
    setSaving(true);
    setError(null);
    try {
      await apiService.updateSupportDeskTicket(id, { account_id: newAccountId });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to update linked account');
      await load();
    } finally {
      setSaving(false);
    }
  };

  const openRequesterEdit = () => {
    setRequesterNameInput(ticket?.requester_name || '');
    setRequesterEmailInput(ticket?.requester_email || '');
    setRequesterEditing(true);
  };

  const saveRequester = async () => {
    if (!id) return;
    setSavingRequester(true);
    try {
      await apiService.updateSupportDeskTicket(id, {
        requester_name: requesterNameInput.trim() || null,
        requester_email: requesterEmailInput.trim() || null,
      });
      await load();
      setRequesterEditing(false);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to update requester');
    } finally {
      setSavingRequester(false);
    }
  };

  const populateRequesterFromAccount = async () => {
    if (!ticket?.account_id) return;
    try {
      const { contacts } = await apiService.getAccountContacts(ticket.account_id);
      const primary = (contacts || []).find((c: AccountContact) => c.contact_type === 'primary') || contacts?.[0];
      if (!primary) return;
      const name = [primary.first_name, primary.last_name].filter(Boolean).join(' ');
      await apiService.updateSupportDeskTicket(id!, {
        requester_name: name || null,
        requester_email: primary.email || null,
      });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to populate requester from account');
    }
  };

  const persistRelatedServiceChange = async (serviceId: string) => {
    if (!id || !ticket) return;
    const newSid = serviceId || null;
    const curSid = ticket.account_service_id ? String(ticket.account_service_id) : null;
    if (newSid === curSid) return;
    setSaving(true);
    setError(null);
    try {
      await apiService.updateSupportDeskTicket(id, { account_service_id: newSid });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to update related service');
      await load();
    } finally {
      setSaving(false);
    }
  };

  const persistDueResolveAtChange = async (d: Date | null) => {
    if (!id || !ticket) return;
    const nextYmd = d ? format(d, 'yyyy-MM-dd') : null;
    const curYmd = dueResolveYmdFromApi(ticket.due_resolve_at);
    if (nextYmd === curYmd) return;
    setSaving(true);
    setError(null);
    try {
      await apiService.updateSupportDeskTicket(id, { due_resolve_at: nextYmd });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to update resolve due date');
      await load();
    } finally {
      setSaving(false);
    }
  };

  const saveMspAllocationOnly = async () => {
    if (!id) return;
    if (mspUseHours) {
      const h = parseFloat(mspHoursInput || '0');
      if (!accountServiceId || Number.isNaN(h) || h <= 0) {
        setError('Select a related MSP service and enter hours greater than zero');
        return;
      }
    }
    const sel = services.find((x) => x.id === accountServiceId);
    if (mspUseHours && sel && !isMspAgreementServiceType(sel.service_type, sel.service_type_name)) {
      setError('Related service must be an MSP agreement to reserve hours');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await apiService.updateSupportDeskTicket(id, {
        account_service_id: accountServiceId || undefined,
        msp_use_hours: mspUseHours,
        msp_hours: mspUseHours ? parseFloat(mspHoursInput || '0') : undefined,
      });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to update MSP allocation');
    } finally {
      setSaving(false);
    }
  };

  const handleUpstreamTicketRefSave = async () => {
    if (!id) return;
    setUpstreamSaving(true);
    try {
      await apiService.updateSupportDeskTicket(id, { upstream_ticket_ref: upstreamTicketRef.trim() || null });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to update upstream ticket reference');
    } finally {
      setUpstreamSaving(false);
    }
  };

  const handleOpenApplyWorkflow = () => {
    setApplyWorkflowTemplateId('');
    apiService.listCaseWorkflowTemplates(true).then((r) => setWorkflowTemplates(r || [])).catch(() => setWorkflowTemplates([]));
    setApplyWorkflowOpen(true);
  };

  const handleApplyWorkflow = async () => {
    if (!id || !applyWorkflowTemplateId) return;
    setApplyWorkflowSubmitting(true);
    try {
      const w = await apiService.applySupportDeskTicketWorkflow(id, applyWorkflowTemplateId);
      setTicketWorkflow({
        id: w.id,
        template_name: w.template_name,
        started_at: w.started_at,
        completed_at: w.completed_at,
        progress_percent: w.progress_percent,
        steps: (w.steps || []).map((s: any) => ({
          id: String(s.id),
          step_name: s.step_name,
          step_description: s.step_description,
          sort_order: s.sort_order,
          status: s.status,
        })),
      });
      setApplyWorkflowOpen(false);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to apply workflow');
    } finally {
      setApplyWorkflowSubmitting(false);
    }
  };

  const handleWorkflowStepStatusChange = async (stepId: string, nextStatus: string) => {
    if (!id || ticketWorkflow?.completed_at) return;
    try {
      await apiService.updateSupportDeskTicketWorkflowStep(id, stepId, { status: nextStatus });
      await fetchWorkflow();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to update step');
    }
  };

  const handleCompleteWorkflow = async () => {
    if (!id) return;
    try {
      const w = await apiService.completeSupportDeskTicketWorkflow(id);
      setTicketWorkflow({
        id: w.id,
        template_name: w.template_name,
        started_at: w.started_at,
        completed_at: w.completed_at,
        progress_percent: w.progress_percent,
        steps: (w.steps || []).map((s: any) => ({
          id: String(s.id),
          step_name: s.step_name,
          step_description: s.step_description,
          sort_order: s.sort_order,
          status: s.status,
        })),
      });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to complete workflow');
    }
  };

  const fetchContactsForEmail = async () => {
    if (!ticket?.account_id) {
      setAccountContacts([]);
      return;
    }
    setContactsLoading(true);
    try {
      const { contacts } = await apiService.getAccountContacts(ticket.account_id);
      setAccountContacts(contacts || []);
    } catch {
      setAccountContacts([]);
    } finally {
      setContactsLoading(false);
    }
  };

  const handleEmailTemplateSelect = (templateId: string) => {
    setSelectedEmailTemplateId(templateId);
    const t = emailTemplates.find((x) => x.id === templateId);
    if (t) {
      setEmailSubject(t.subject || '');
      setEmailBody('');
    } else {
      setEmailBody('');
    }
  };

  const handleSendEmail = async () => {
    if (!id || !emailTo.trim() || !emailSubject.trim()) return;
    const userMessage = emailBody.trim();
    if (!userMessage) return;
    setSendingEmail(true);
    try {
      const selectedTemplate = selectedEmailTemplateId
        ? emailTemplates.find((x) => x.id === selectedEmailTemplateId)
        : null;
      const { body: bodySend, body_html: bodyHtmlSend } = buildSupportDeskTicketEmailBodies(
        userMessage,
        selectedTemplate || undefined
      );
      await apiService.sendSupportDeskTicketEmail(id, {
        to_email: emailTo.trim(),
        cc_email: emailCc.trim() || undefined,
        bcc_email: emailBcc.trim() || undefined,
        subject: emailSubject,
        body: bodySend,
        body_html: bodyHtmlSend,
        template_id: selectedEmailTemplateId || undefined,
      });
      setEmailOpen(false);
      setEmailTo('');
      setEmailCc('');
      setEmailBcc('');
      setEmailSubject('');
      setEmailBody('');
      setSelectedEmailTemplateId('');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!id || !file) return;
    setUploading(true);
    try {
      await apiService.uploadSupportDeskTicketAttachment(id, file);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handlePreviewAttachment = (a: { id: string; filename: string; content_type?: string | null }) => {
    if (!id) return;
    setPreviewAttachment({ id: a.id, filename: a.filename, content_type: a.content_type });
    setPreviewUrl(null);
    setPreviewTextContent(null);
    setPreviewOpen(true);
    setPreviewLoading(true);
    const apiUrl = `${process.env.REACT_APP_API_URL || window.location.origin}/api/support-desk/tickets/${id}/attachments/${a.id}/download`;
    const token = localStorage.getItem('access_token');
    fetch(apiUrl, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob())
      .then((blob) => {
        const ct = (a.content_type || '').toLowerCase();
        const ext = a.filename.split('.').pop()?.toLowerCase() || '';
        const isText =
          ct.startsWith('text/') ||
          ct.includes('json') ||
          ct.includes('xml') ||
          ['txt', 'json', 'xml', 'csv', 'log', 'md', 'yaml', 'yml'].includes(ext);
        if (isText) {
          blob.text().then((text) => {
            setPreviewTextContent(text);
            setPreviewLoading(false);
          });
        } else {
          setPreviewUrl(URL.createObjectURL(blob));
          setPreviewLoading(false);
        }
      })
      .catch(() => {
        setError('Failed to load preview');
        setPreviewLoading(false);
      });
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewTextContent(null);
    setPreviewAttachment(null);
  };

  const handleDownloadAttachment = (a: { id: string; filename: string }) => {
    if (!id) return;
    const url = `${process.env.REACT_APP_API_URL || window.location.origin}/api/support-desk/tickets/${id}/attachments/${a.id}/download`;
    const token = localStorage.getItem('access_token');
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const lnk = document.createElement('a');
        lnk.href = u;
        lnk.download = a.filename;
        lnk.click();
        URL.revokeObjectURL(u);
      })
      .catch(() => setError('Failed to download'));
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!id) return;
    try {
      await apiService.deleteSupportDeskTicketAttachment(id, attachmentId);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to delete attachment');
    }
  };

  const openAccountInNewTab = () => {
    if (!ticket?.account_id) return;
    window.open(`${window.location.origin}/accounts/${ticket.account_id}`, '_blank', 'noopener,noreferrer');
  };

  const openRelatedServiceInNewTab = () => {
    if (!ticket?.account_id || !accountServiceId) return;
    const q = new URLSearchParams({ tab: 'services', service: accountServiceId });
    window.open(`${window.location.origin}/accounts/${ticket.account_id}?${q.toString()}`, '_blank', 'noopener,noreferrer');
  };

  if (loading && !ticket) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (!ticket) {
    return <Alert severity="error">{error || 'Not found'}</Alert>;
  }

  const selectedSvc = services.find((x) => x.id === accountServiceId);
  const mspEligible = selectedSvc && isMspAgreementServiceType(selectedSvc.service_type, selectedSvc.service_type_name);
  const relatedServiceSummaryLabel =
    selectedSvc != null
      ? `${selectedSvc.service_id} (${selectedSvc.service_type})`
      : ticket.account_service_display || ticket.account_service_id || '—';

  const richHtmlSx = {
    mb: 0,
    maxWidth: '100%',
    minWidth: 0,
    fontSize: '0.875rem',
    '& p': { margin: '0 0 0.35em 0' },
    '& p:last-child': { marginBottom: 0 },
    '& ul, & ol': { margin: '0.35em 0', paddingLeft: 1.5 },
    '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1, display: 'block' },
    '& table': { maxWidth: '100%', display: 'block', overflow: 'auto' },
    '& a': { wordBreak: 'break-all' as const },
  };

  const activityRichSx = {
    mt: 0.5,
    fontSize: '0.875rem',
    '& p': { margin: '0 0 0.35em 0' },
    '& p:last-child': { marginBottom: 0 },
    '& ul, & ol': { margin: '0.35em 0', paddingLeft: 1.5 },
    '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1, display: 'block' },
    '& table': { maxWidth: '100%', display: 'block', overflow: 'auto' },
    '& a': { wordBreak: 'break-all' as const },
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate(-1)} size="small">
          <BackIcon />
        </IconButton>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'baseline',
            gap: 1,
            flex: '1 1 280px',
            minWidth: 0,
            columnGap: 1,
            rowGap: 0.25,
          }}
        >
          <Typography component="span" variant="body2" fontWeight={600} color="text.secondary">
            {ticket.ticket_number}
          </Typography>
          <Typography component="span" variant="h6" fontWeight={600} sx={{ flex: '1 1 200px', minWidth: 0, lineHeight: 1.35 }}>
            {ticket.subject || '—'}
          </Typography>
        </Box>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {ticket.merged_into_ticket_id && ticket.merged_into_ticket_number && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This ticket was merged into{' '}
          <Button
            size="small"
            variant="text"
            sx={{ textTransform: 'none', fontWeight: 600, p: 0, minWidth: 0, verticalAlign: 'baseline' }}
            onClick={() => navigate(`/crm/support-desk/${ticket.merged_into_ticket_id}`)}
          >
            {ticket.merged_into_ticket_number}
          </Button>
          . Use the parent ticket for replies, notes, and workflow.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 1.5,
            }}
          >
            <Grid container spacing={1.5} alignItems="flex-start">
              {/* Account — full width; primary anchor */}
              <Grid item xs={12}>
                <Typography variant="overline" color="text.secondary" letterSpacing="0.06em" display="block" sx={{ mb: 0.5 }}>
                  Linked customer account
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant="body1" fontWeight={600} sx={{ flex: '1 1 120px', minWidth: 0 }}>
                    {ticket.account_sonic_number
                      ? `${ticket.account_sonic_number} — ${ticket.account_display_name || ''}`.trim()
                      : ticket.account_display_name || ticket.account_id || '—'}
                  </Typography>
                  {ticket.account_id && (
                    <IconButton
                      size="small"
                      onClick={openAccountInNewTab}
                      title="Open account in new tab"
                      aria-label="Open account in new tab"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Autocomplete
                  size="small"
                  sx={{ mt: 0.5 }}
                  options={accountOptions}
                  value={accountLink}
                  inputValue={accountSearch}
                  onInputChange={(_, newInput) => setAccountSearch(newInput)}
                  onChange={(_, newVal) => {
                    setAccountLink(newVal);
                    if (newVal) {
                      setAccountSearch(formatAccountLinkLabel(newVal));
                    } else {
                      setAccountSearch('');
                      setAccountOptions([]);
                    }
                    void persistAccountLinkChange(newVal);
                  }}
                  getOptionLabel={(o) => formatAccountLinkLabel(o)}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  filterOptions={(x) => x}
                  noOptionsText={
                    accountSearch.trim().length < 2
                      ? 'Type at least 2 characters (e.g. Sonic account number)'
                      : 'No accounts found'
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search accounts"
                      placeholder="Sonic #, name, or email…"
                      helperText="Selecting an account saves immediately; related service is cleared when the account changes."
                    />
                  )}
                />
              </Grid>

              {/* Requester — editable */}
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                  <Typography variant="overline" color="text.secondary" letterSpacing="0.06em">
                    Requester
                  </Typography>
                  {!requesterEditing && (
                    <Tooltip title="Edit requester">
                      <IconButton size="small" onClick={openRequesterEdit} sx={{ p: 0.25 }}>
                        <EditIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {!requesterEditing && ticket.account_id && !ticket.requester_email?.trim() && (
                    <Tooltip title="Populate from linked account's primary contact">
                      <IconButton size="small" onClick={populateRequesterFromAccount} sx={{ p: 0.25 }}>
                        <PersonAddIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                {!requesterEditing ? (
                  <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-word' }}>
                    {ticket.requester_name
                      ? `${ticket.requester_name}${ticket.requester_email ? ` <${ticket.requester_email}>` : ''}`
                      : ticket.requester_email || '—'}
                  </Typography>
                ) : (
                  <Collapse in={requesterEditing}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
                      <TextField
                        size="small"
                        label="Name"
                        value={requesterNameInput}
                        onChange={(e) => setRequesterNameInput(e.target.value)}
                        disabled={savingRequester}
                        fullWidth
                      />
                      <TextField
                        size="small"
                        label="Email"
                        type="email"
                        value={requesterEmailInput}
                        onChange={(e) => setRequesterEmailInput(e.target.value)}
                        disabled={savingRequester}
                        fullWidth
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={savingRequester ? <CircularProgress size={14} /> : <SaveIcon />}
                          onClick={saveRequester}
                          disabled={savingRequester}
                        >
                          Save
                        </Button>
                        <Button size="small" onClick={() => setRequesterEditing(false)} disabled={savingRequester}>
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  </Collapse>
                )}
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="overline" color="text.secondary" letterSpacing="0.06em" display="block" sx={{ mb: 0.25 }}>
                  First response due
                </Typography>
                <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ fontSize: '0.875rem' }}>
                  {formatDateTime(ticket.due_first_response_at)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
                  <DatePicker
                    format="dd/MM/yyyy"
                    label="Resolve due"
                    value={dueResolveAtToPickerDate(ticket.due_resolve_at)}
                    onChange={(v) => void persistDueResolveAtChange(v)}
                    disabled={saving}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        margin: 'none',
                        helperText: 'Saves on change (date only)',
                      },
                    }}
                    sx={{ width: '100%' }}
                  />
                </LocalizationProvider>
              </Grid>

              {/* Status, priority, assignee — one row on sm+ */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel id="sd-ticket-status-label">Status</InputLabel>
                  <Select
                    labelId="sd-ticket-status-label"
                    label="Status"
                    value={status}
                    onChange={(e) => handleInlineStatusChange(e.target.value)}
                    disabled={inlineSaving === 'status'}
                    renderValue={(v) => {
                      const lbl = statusLabelMap[v] || stOpts.find((o) => o.value === v)?.label || v;
                      return <Chip label={lbl} size="small" color={statusColorMap[v] ?? 'default'} sx={{ height: 22 }} />;
                    }}
                  >
                    {stOpts.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel id="sd-ticket-priority-label">Priority</InputLabel>
                  <Select
                    labelId="sd-ticket-priority-label"
                    label="Priority"
                    value={priority}
                    onChange={(e) => handleInlinePriorityChange(e.target.value)}
                    disabled={inlineSaving === 'priority'}
                    renderValue={(v) => {
                      const lbl = priorityLabelMap[v] || prOpts.find((o) => o.value === v)?.label || v;
                      return (
                        <Chip label={lbl} size="small" color={priorityColorMap[v] ?? 'default'} sx={{ height: 22 }} />
                      );
                    }}
                  >
                    {prOpts.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel id="sd-ticket-assignee-label">Assignee</InputLabel>
                  <Select
                    labelId="sd-ticket-assignee-label"
                    label="Assignee"
                    value={assigneeId}
                    onChange={(e) => handleInlineAssigneeChange(e.target.value)}
                    disabled={inlineSaving === 'assignee'}
                  >
                    <MenuItem value="">(none)</MenuItem>
                    {users.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.display_name || u.username}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {services.length > 0 && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel>Related service</InputLabel>
                    <Select
                      value={accountServiceId || ''}
                      label="Related service"
                      onChange={(e) => {
                        const v = e.target.value || '';
                        setAccountServiceId(v);
                        setMspUseHours(false);
                        setMspHoursInput('');
                        void persistRelatedServiceChange(v);
                      }}
                    >
                      <MenuItem value="">None</MenuItem>
                      {services.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.service_id} ({s.service_type})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {(accountServiceId || ticket.account_service_display || ticket.account_service_id) && (
                <Grid item xs={12} md={services.length > 0 ? 6 : 12}>
                  <Typography variant="overline" color="text.secondary" letterSpacing="0.06em" display="block" sx={{ mb: 0.25 }}>
                    Linked service
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ flex: '1 1 120px', minWidth: 0 }}>
                      {relatedServiceSummaryLabel}
                    </Typography>
                    {accountServiceId && ticket.account_id && (
                      <IconButton
                        size="small"
                        onClick={openRelatedServiceInNewTab}
                        title="Open service on account in new tab"
                        aria-label="Open service on account in new tab"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Grid>
              )}

              {accountServiceId && mspEligible ? (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 1.5,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                    }}
                  >
                    {mspBalanceHint && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {mspBalanceHint}
                      </Typography>
                    )}
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={mspUseHours}
                          onChange={(e) => setMspUseHours(e.target.checked)}
                          disabled={saving}
                        />
                      }
                      label="Use MSP agreement hours"
                    />
                    {mspUseHours && (
                      <TextField
                        label="Hours reserved"
                        type="number"
                        size="small"
                        fullWidth
                        inputProps={{ step: 0.25, min: 0 }}
                        value={mspHoursInput}
                        onChange={(e) => setMspHoursInput(e.target.value)}
                        sx={{ mt: 1, maxWidth: 280 }}
                        disabled={saving}
                      />
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ mt: 1, display: 'block' }}
                      onClick={saveMspAllocationOnly}
                      disabled={saving}
                    >
                      {saving ? 'Saving…' : 'Save MSP allocation'}
                    </Button>
                  </Box>
                </Grid>
              ) : null}

              <Grid item xs={12}>
                <Typography variant="overline" color="text.secondary" letterSpacing="0.06em" display="block" sx={{ mb: 0.25 }}>
                  Upstream ticket ref
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="e.g. Ticket #70607, VOC-12345"
                    value={upstreamTicketRef}
                    onChange={(e) => setUpstreamTicketRef(e.target.value)}
                    disabled={upstreamSaving || !!ticket?.merged_into_ticket_id}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleUpstreamTicketRefSave(); }}
                    inputProps={{ maxLength: 500 }}
                  />
                  <Button size="small" variant="outlined" onClick={handleUpstreamTicketRefSave} disabled={upstreamSaving || !!ticket?.merged_into_ticket_id}>
                    {upstreamSaving ? 'Saving…' : 'Save'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Workflow
            </Typography>
            {workflowLoading ? (
              <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : !ticketWorkflow ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Apply a workflow template to track steps (open, in progress, complete) and see progress.
                </Typography>
                <Button fullWidth variant="outlined" size="small" onClick={handleOpenApplyWorkflow}>
                  Apply workflow
                </Button>
              </>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    flexWrap: 'wrap',
                    mb: 0.5,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {ticketWorkflow.template_name}
                  </Typography>
                  {ticketWorkflow.completed_at && (
                    <Chip size="small" label="Completed" color="success" variant="outlined" />
                  )}
                </Box>
                {ticketWorkflow.completed_at && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Finished {new Date(ticketWorkflow.completed_at).toLocaleString()}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={ticketWorkflow.completed_at ? 100 : ticketWorkflow.progress_percent}
                    sx={{
                      flex: 1,
                      height: 8,
                      borderRadius: 1,
                      ...(ticketWorkflow.completed_at
                        ? { '& .MuiLinearProgress-bar': { bgcolor: 'success.main' } }
                        : {}),
                    }}
                  />
                  <Typography variant="caption" fontWeight={600}>
                    {ticketWorkflow.completed_at ? 100 : Math.round(ticketWorkflow.progress_percent)}%
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  {ticketWorkflow.steps.filter((s) => s.status === 'completed').length} of {ticketWorkflow.steps.length}{' '}
                  steps
                </Typography>
                {ticketWorkflow.completed_at && (
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Completion recorded in activity below.
                  </Typography>
                )}
                <List dense disablePadding sx={{ mb: 1 }}>
                  {ticketWorkflow.steps
                    .slice()
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map((step, index, sorted) => {
                      const isCompleted = step.status === 'completed';
                      const isNextOpen =
                        !isCompleted && sorted.slice(0, index).every((s) => s.status === 'completed');
                      return (
                        <ListItem
                          key={step.id}
                          disablePadding
                          sx={{ alignItems: 'flex-start', gap: 1, py: 0.5, position: 'relative', pl: 0 }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              mr: 1.5,
                              mt: 0.25,
                            }}
                          >
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                ...(isCompleted
                                  ? { color: 'success.main', bgcolor: 'success.light' }
                                  : isNextOpen
                                    ? {
                                        border: '2px solid',
                                        borderColor: 'primary.main',
                                        bgcolor: 'primary.main',
                                        color: 'primary.contrastText',
                                        boxShadow: 1,
                                      }
                                    : {
                                        border: '2px solid',
                                        borderColor: 'action.disabled',
                                        bgcolor: 'action.hover',
                                      }),
                              }}
                            >
                              {isCompleted ? (
                                <CheckCircleIcon sx={{ fontSize: 18 }} />
                              ) : (
                                <Typography variant="caption" fontWeight={700}>
                                  {index + 1}
                                </Typography>
                              )}
                            </Box>
                            {index < sorted.length - 1 && (
                              <Box
                                sx={{
                                  width: 2,
                                  minHeight: 16,
                                  bgcolor: isCompleted ? 'success.light' : 'action.disabledBackground',
                                  borderRadius: 1,
                                }}
                              />
                            )}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ListItemText
                              primary={step.step_name}
                              secondary={step.step_description}
                              primaryTypographyProps={{
                                variant: 'body2',
                                fontWeight: isNextOpen ? 600 : undefined,
                                color: isNextOpen ? 'primary.main' : undefined,
                              }}
                              sx={{ flex: 1, minWidth: 0 }}
                            />
                            <FormControl size="small" sx={{ minWidth: 120, flexShrink: 0 }}>
                              <Select
                                value={step.status}
                                onChange={(e) => handleWorkflowStepStatusChange(step.id, e.target.value)}
                                displayEmpty
                                disabled={Boolean(ticketWorkflow.completed_at)}
                              >
                                <MenuItem value="open">Open</MenuItem>
                                <MenuItem value="in_progress">In progress</MenuItem>
                                <MenuItem value="completed">Complete</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                        </ListItem>
                      );
                    })}
                </List>
                {!ticketWorkflow.completed_at &&
                  ticketWorkflow.steps.length > 0 &&
                  ticketWorkflow.steps.every((s) => s.status === 'completed') && (
                    <Button fullWidth size="small" variant="contained" color="primary" onClick={handleCompleteWorkflow}>
                      Mark workflow complete
                    </Button>
                  )}
                {ticketWorkflow.completed_at && (
                  <Button fullWidth size="small" variant="outlined" onClick={handleOpenApplyWorkflow} sx={{ mt: 1 }}>
                    Apply another workflow
                  </Button>
                )}
              </>
            )}
          </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Actions
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<EmailIcon />}
                onClick={() => {
                  setEmailOpen(true);
                  setEmailTo(ticket.requester_email || '');
                  setEmailCc('');
                  setEmailBcc('');
                  setEmailSubject('');
                  setEmailBody('');
                  setSelectedEmailTemplateId('');
                  fetchContactsForEmail();
                }}
                sx={{ mb: 1 }}
              >
                Send email
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AttachIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? 'Uploading…' : 'Upload attachment'}
              </Button>
              <input ref={fileInputRef} type="file" hidden onChange={handleFileUpload} />
            </Paper>

            {ticket.attachments && ticket.attachments.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Attachments
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Filename</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(ticket.attachments as { id: string; filename: string; content_type?: string | null }[]).map(
                        (att) => (
                          <TableRow key={att.id}>
                            <TableCell>{att.filename}</TableCell>
                            <TableCell align="right">
                              <IconButton size="small" onClick={() => handlePreviewAttachment(att)} title="Preview">
                                <PreviewIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={() => handleDownloadAttachment(att)} title="Download">
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteAttachment(att.id)}
                                title="Delete"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        )
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Description
            </Typography>
            {ticket.description ? (
              <Box
                sx={{
                  height: { xs: 'min(360px, 50vh)', sm: 400 },
                  maxHeight: '55vh',
                  overflowY: 'auto',
                  overflowX: 'auto',
                  minWidth: 0,
                  pr: 0.5,
                  boxSizing: 'border-box',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1.5,
                  bgcolor: 'action.hover',
                }}
              >
                {looksLikeHtml(ticket.description) ? (
                  <EmailHtmlContent
                    className="support-desk-html-description"
                    sx={richHtmlSx}
                    html={ticket.description}
                  />
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                  >
                    {ticket.description}
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Activity timeline
            </Typography>
            <Box sx={{ mb: 2 }}>
              <ToggleButtonGroup
                exclusive
                value={activityComposerMode}
                onChange={(_, v: 'internal' | 'email' | null) => {
                  if (v === null) return;
                  setActivityComposerMode(v);
                  setError(null);
                  if (v === 'email') {
                    setTimelineReplyTemplateId('');
                    setTimelineReplySubject('');
                  }
                }}
                size="small"
                sx={{ mb: 1.5, display: 'flex', flexWrap: 'wrap' }}
              >
                <ToggleButton value="internal">Internal note</ToggleButton>
                <ToggleButton
                  value="email"
                  disabled={Boolean(ticket?.merged_into_ticket_id) || !ticket?.requester_email?.trim()}
                >
                  Email customer
                </ToggleButton>
              </ToggleButtonGroup>
              {activityComposerMode === 'email' && (
                <Box sx={{ mb: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel id="sd-timeline-reply-template-label">Email template</InputLabel>
                    <Select
                      labelId="sd-timeline-reply-template-label"
                      label="Email template"
                      value={timelineReplyTemplateId || ''}
                      onChange={(e) => {
                        const tid = String(e.target.value);
                        setTimelineReplyTemplateId(tid);
                        const t = emailTemplates.find((x) => x.id === tid);
                        setTimelineReplySubject(t?.subject || '');
                      }}
                      displayEmpty
                      required
                    >
                      {emailTemplates.length === 0 ? (
                        <MenuItem value="" disabled>
                          No active templates (System Settings → Support Desk Module)
                        </MenuItem>
                      ) : (
                        emailTemplates.map((t) => (
                          <MenuItem key={t.id} value={t.id}>
                            {t.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Subject"
                    size="small"
                    fullWidth
                    value={timelineReplySubject}
                    onChange={(e) => setTimelineReplySubject(e.target.value)}
                    placeholder="Subject line ({{placeholders}} substituted on send)"
                  />
                  <Typography variant="caption" color="text.secondary" display="block">
                    To: {ticket.requester_email || '—'}. Subject/body: {TICKET_EMAIL_PLACEHOLDERS.join(', ')}. Body
                    wrapper: {SUPPORT_DESK_EMAIL_BODY_PLACEHOLDER} inserts the message below.
                  </Typography>
                  {(!ticket?.requester_email?.trim() || ticket?.merged_into_ticket_id) && (
                    <Typography variant="caption" color="warning.main" display="block">
                      {ticket?.merged_into_ticket_id
                        ? 'Merged tickets cannot receive email from this page.'
                        : 'Set a requester email on the ticket to enable customer email.'}
                    </Typography>
                  )}
                </Box>
              )}
              {activityComposerMode === 'internal' && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Visible only to staff (not emailed to the requester).
                </Typography>
              )}
              <Box sx={{ minHeight: 120, mb: 1 }}>
                <ReactQuill
                  theme="snow"
                  value={note}
                  onChange={setNote}
                  placeholder={
                    activityComposerMode === 'internal'
                      ? 'Add an internal note…'
                      : 'Write your reply (inserted into the template at %%email_body%% when the template uses it)…'
                  }
                  style={{ minHeight: 100 }}
                  modules={NOTE_QUILL_MODULES}
                />
              </Box>
              <Button
                variant="contained"
                onClick={submitActivityComposer}
                disabled={
                  saving ||
                  isQuillNoteEmpty(note) ||
                  (activityComposerMode === 'email' &&
                    (!timelineReplyTemplateId ||
                      !timelineReplySubject.trim() ||
                      !ticket?.requester_email?.trim() ||
                      Boolean(ticket?.merged_into_ticket_id)))
                }
              >
                {saving ? (
                  <CircularProgress size={24} />
                ) : activityComposerMode === 'internal' ? (
                  'Add internal note'
                ) : (
                  'Send to customer'
                )}
              </Button>
            </Box>
            <Divider sx={{ mb: 1 }} />
            <List disablePadding>
              {(ticket.activity || []).map((a: any, idx: number) => (
                <React.Fragment key={a.id}>
                  {idx > 0 && <Divider />}
                  <ListItem alignItems="flex-start" sx={{ py: 1, bgcolor: supportDeskActivityBackground(a.activity_type) }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                        {getSupportDeskActivityIcon(a.activity_type)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primaryTypographyProps={{ variant: 'body2', component: 'div' }}
                      primary={
                        <>
                          <Typography variant="body2" fontWeight={600} component="span" display="block">
                            {supportDeskActivityLabel(a.activity_type)}
                            {a.user_name ? ` · ${a.user_name}` : ''}
                          </Typography>
                          {a.activity_type === 'note' && a.content ? (
                            <Box
                              component="div"
                              className="support-desk-note-content"
                              sx={{
                                mt: 0.5,
                                fontSize: '0.875rem',
                                '& p': { margin: '0 0 0.35em 0' },
                                '& p:last-child': { marginBottom: 0 },
                                '& ul, & ol': { margin: '0.35em 0', paddingLeft: 1.5 },
                                '& img': { maxWidth: '100%', height: 'auto', borderRadius: 4, display: 'block' },
                              }}
                              dangerouslySetInnerHTML={{ __html: a.content }}
                            />
                          ) : (a.activity_type === 'customer_reply' || a.activity_type === 'email_sent') &&
                            a.metadata ? (
                            <Box>
                              <Typography component="span" variant="body2">
                                {a.content || a.activity_type}
                              </Typography>
                              {(() => {
                                const m = a.metadata as Record<string, unknown>;
                                const bodyRaw = m.body
                                  ? String(m.body)
                                  : m.body_html
                                    ? stripHtml(String(m.body_html))
                                    : '';
                                const previewLen = 150;
                                const hasMore = bodyRaw.length > previewLen;
                                const preview = bodyRaw
                                  ? (hasMore ? bodyRaw.slice(0, previewLen).trim() + '…' : bodyRaw)
                                  : null;
                                const openEmailModal = () => {
                                  const isReply = a.activity_type === 'customer_reply';
                                  setEmailViewData({
                                    to: String(isReply ? (m.from || '') : (m.to || '')),
                                    subject: String(m.subject || ''),
                                    cc: m.cc ? String(m.cc) : undefined,
                                    bcc: m.bcc ? String(m.bcc) : undefined,
                                    body: m.body
                                      ? String(m.body)
                                      : m.full_email
                                        ? String(m.full_email)
                                        : undefined,
                                    body_html: m.body_html ? String(m.body_html) : undefined,
                                    isReply,
                                  });
                                  setEmailViewOpen(true);
                                };
                                return (
                                  <>
                                    {preview && (
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 0.5, fontSize: '0.8125rem' }}
                                      >
                                        {preview}
                                      </Typography>
                                    )}
                                    <Button
                                      size="small"
                                      startIcon={<ExpandMoreIcon />}
                                      onClick={openEmailModal}
                                      sx={{ mt: 0.25, p: 0, minWidth: 0, fontSize: '0.75rem' }}
                                    >
                                      View full email
                                    </Button>
                                  </>
                                );
                              })()}
                            </Box>
                          ) : typeof a.metadata?.body_html === 'string' && a.metadata.body_html.trim() ? (
                            <EmailHtmlContent
                              className="support-desk-email-html"
                              sx={activityRichSx}
                              html={a.metadata.body_html}
                            />
                          ) : (
                            <Typography variant="body2" component="div" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                              {(typeof a.metadata?.body === 'string' ? a.metadata.body : '') || a.content || ''}
                            </Typography>
                          )}
                        </>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: '0.75rem', mt: 0.5, display: 'block' }}
                        >
                          {a.created_at ? new Date(a.created_at).toLocaleString() : ''}
                        </Typography>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
            {(!ticket.activity || ticket.activity.length === 0) && (
              <Typography color="text.secondary">No activity yet</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: '70vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {previewAttachment?.filename}
          <Box>
            <Button
              startIcon={<DownloadIcon />}
              onClick={() =>
                previewAttachment &&
                handleDownloadAttachment({ id: previewAttachment.id, filename: previewAttachment.filename })
              }
              size="small"
              sx={{ mr: 1 }}
            >
              Download
            </Button>
            <IconButton size="small" onClick={handleClosePreview} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 400,
            bgcolor: 'grey.100',
          }}
        >
          {previewLoading && <CircularProgress />}
          {!previewLoading &&
            previewAttachment &&
            (() => {
              const ct = (previewAttachment.content_type || '').toLowerCase();
              const ext = previewAttachment.filename.split('.').pop()?.toLowerCase() || '';
              const isImage =
                ct.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext);
              const isPdf = ct === 'application/pdf' || ext === 'pdf';
              const isText =
                ct.startsWith('text/') ||
                ct.includes('json') ||
                ct.includes('xml') ||
                ['txt', 'json', 'xml', 'csv', 'log', 'md', 'yaml', 'yml'].includes(ext);
              if (isImage && previewUrl) {
                return (
                  <Box
                    component="img"
                    src={previewUrl}
                    alt={previewAttachment.filename}
                    sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                  />
                );
              }
              if (isPdf && previewUrl) {
                return (
                  <iframe
                    src={previewUrl}
                    title={previewAttachment.filename}
                    style={{ width: '100%', height: '70vh', border: 'none' }}
                  />
                );
              }
              if (isText && previewTextContent !== null) {
                return (
                  <Box sx={{ width: '100%', height: '70vh', overflow: 'auto', p: 2 }}>
                    <Typography
                      component="pre"
                      sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {previewTextContent}
                    </Typography>
                  </Box>
                );
              }
              if (!isImage && !isPdf && !isText) {
                return (
                  <Box sx={{ textAlign: 'center', p: 3 }}>
                    <Typography color="text.secondary">Preview not available for this file type.</Typography>
                    <Button
                      startIcon={<DownloadIcon />}
                      onClick={() =>
                        previewAttachment &&
                        handleDownloadAttachment({
                          id: previewAttachment.id,
                          filename: previewAttachment.filename,
                        })
                      }
                      sx={{ mt: 2 }}
                    >
                      Download file
                    </Button>
                  </Box>
                );
              }
              return null;
            })()}
        </DialogContent>
      </Dialog>

      <Dialog open={applyWorkflowOpen} onClose={() => setApplyWorkflowOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Apply workflow</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Workflow template</InputLabel>
            <Select
              value={applyWorkflowTemplateId}
              label="Workflow template"
              onChange={(e) => setApplyWorkflowTemplateId(e.target.value)}
            >
              <MenuItem value="">Select a workflow</MenuItem>
              {workflowTemplates.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyWorkflowOpen(false)}>Cancel</Button>
          <Button
            onClick={handleApplyWorkflow}
            variant="contained"
            disabled={!applyWorkflowTemplateId || applyWorkflowSubmitting}
          >
            {applyWorkflowSubmitting ? <CircularProgress size={20} /> : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={emailOpen} onClose={() => setEmailOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Send email</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1, minHeight: 360 }}>
            <FormControl fullWidth>
              <InputLabel>Template (optional)</InputLabel>
              <Select
                value={selectedEmailTemplateId}
                label="Template"
                onChange={(e) => handleEmailTemplateSelect(e.target.value)}
              >
                <MenuItem value="">Custom</MenuItem>
                {emailTemplates.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              freeSolo
              options={accountContacts
                .filter((c) => c.email)
                .map((c) => ({ email: c.email!, label: `${c.full_name || 'Contact'} <${c.email}>` }))}
              getOptionLabel={(opt) =>
                typeof opt === 'string' ? opt : (opt as { email: string; label: string }).label
              }
              inputValue={emailTo}
              onInputChange={(_, v) => setEmailTo(v)}
              onChange={(_, v) =>
                setEmailTo(typeof v === 'string' ? v : (v as { email: string } | null)?.email ?? '')
              }
              loading={contactsLoading}
              renderInput={(params) => (
                <TextField {...params} label="To" required placeholder="Select contact or type email" />
              )}
            />
            <TextField label="CC" value={emailCc} onChange={(e) => setEmailCc(e.target.value)} fullWidth placeholder="Optional" />
            <TextField
              label="BCC"
              value={emailBcc}
              onChange={(e) => setEmailBcc(e.target.value)}
              fullWidth
              placeholder="Optional"
            />
            <TextField
              label="Subject"
              required
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              fullWidth
              placeholder="e.g. Re: {{ticket_number}} – {{subject}}"
            />
            <Box sx={{ minHeight: 200 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Body
              </Typography>
              <ReactQuill theme="snow" value={emailBody} onChange={setEmailBody} style={{ minHeight: 180 }} />
            </Box>
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Available placeholders (subject and body)
              </Typography>
              <Typography variant="caption" component="span" sx={{ wordBreak: 'break-all' }}>
                {TICKET_EMAIL_PLACEHOLDERS.join(', ')}. Body only: {SUPPORT_DESK_EMAIL_BODY_PLACEHOLDER} (inserts your message).
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSendEmail}
            disabled={sendingEmail || !emailTo.trim() || !emailSubject.trim() || !emailBody.trim()}
          >
            {sendingEmail ? <CircularProgress size={24} /> : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={emailViewOpen} onClose={() => setEmailViewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{emailViewData?.isReply ? 'Customer reply' : 'Sent email'}</DialogTitle>
        <DialogContent>
          {emailViewData && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {emailViewData.isReply ? 'From' : 'To'}
                </Typography>
                <Typography variant="body2">{emailViewData.to}</Typography>
              </Box>
              {emailViewData.cc && (
                <Box>
                  <Typography variant="caption" color="text.secondary">CC</Typography>
                  <Typography variant="body2">{emailViewData.cc}</Typography>
                </Box>
              )}
              {emailViewData.bcc && (
                <Box>
                  <Typography variant="caption" color="text.secondary">BCC</Typography>
                  <Typography variant="body2">{emailViewData.bcc}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">Subject</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {emailViewData.subject}
                </Typography>
              </Box>
              <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">Body</Typography>
                <Box
                  sx={{
                    mt: 0.5,
                    maxHeight: 300,
                    overflow: 'auto',
                    fontSize: '0.875rem',
                    '& p': { margin: '0 0 0.35em 0' },
                  }}
                >
                  {emailViewData.body_html ? (
                    <EmailHtmlContent html={emailViewData.body_html} />
                  ) : (
                    <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', m: 0 }}>
                      {emailViewData.body || '(No body stored)'}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupportDeskDetailPage;
